import { useEffect, useRef, useState } from "react"
import axios from "axios"
import Swal from "sweetalert2"
import "./BulkCandidateStageModal.css"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"
import { FaTimes } from "react-icons/fa"

export const applicationId = value => {
  if (typeof value !== "number" && typeof value !== "string") return null
  if (typeof value === "string" && !/^\d+$/.test(value.trim())) return null
  const id = Number(value)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

const rejectionRounds = ["L1 Round", "L2 Round", "By HR"]
const errorText = value => {
  if (value == null) return "Request failed."
  if (typeof value === "string") return value
  if (Array.isArray(value)) return value.map(errorText).join("; ")
  return Object.entries(value).map(([key, item]) => `${key}: ${errorText(item)}`).join("; ")
}

export default function BulkCandidateStageModal({ jobId, ids, candidates = [], stages, onClose, onProcessed, onRefresh, showSharedMode = false, selectedCompanyId, authToken }) {
  const [stage, setStage] = useState("")
  const [reason, setReason] = useState("")
  const [round, setRound] = useState("")
  const [mode, setMode] = useState(showSharedMode ? "same" : "different")
  const [individual, setIndividual] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")
  const [outcome, setOutcome] = useState(null)
  const [needsRefresh, setNeedsRefresh] = useState(false)
  const requestLock = useRef(false)
  useEffect(() => {
    setStage(""); setReason(""); setRound(""); setError(""); setOutcome(null); setNeedsRefresh(false)
    setMode(showSharedMode ? "same" : "different"); setIndividual({})
    setSubmitted(false)
  }, [jobId, showSharedMode])
  const uniqueIds = [...new Set(ids.map(applicationId).filter(Boolean))]
  const candidateName = id => candidates.find(candidate => applicationId(candidate.id) === id)?.name || `Application ${id}`
  const updateIndividual = (id, key, value) => setIndividual(previous => ({
    ...previous, [id]: { ...previous[id], [key]: value },
  }))
  const fieldMessage = (field, value) => {
    if (!submitted) return ""
    if (!String(value || "").trim()) return "This field is required"
    if (field === "stage" && !stages.some(item => item.name === value)) return "Select a valid stage"
    if (field === "round" && !rejectionRounds.includes(value)) return "Select a valid rejected round"
    return ""
  }
  const fieldProps = (id, field, value, className) => ({
    className: `${className}${fieldMessage(field, value) ? " is-invalid" : ""}`,
    "aria-invalid": Boolean(fieldMessage(field, value)),
    "aria-describedby": fieldMessage(field, value) ? `${id}-error` : undefined,
  })
  const fieldError = (id, field, value) => fieldMessage(field, value) &&
    <div id={`${id}-error`} className="invalid-feedback d-block" role="alert">{fieldMessage(field, value)}</div>
  const refresh = async () => {
    requestLock.current = true
    setPending(true)
    try {
      await onRefresh(jobId)
      setNeedsRefresh(false)
      setError("")
    } catch {
      setError("Unable to refresh. Refresh the page before submitting another request.")
    } finally { requestLock.current = false; setPending(false) }
  }
  const submit = async event => {
    event.preventDefault()
    if (requestLock.current || needsRefresh) return
    setSubmitted(true)
    if (!uniqueIds.length || uniqueIds.length > 100 || uniqueIds.length !== ids.length) {
      setError("Select 1 to 100 unique positive job application IDs."); return
    }
    const valid = values => stages.some(item => item.name === values.stage) && values.reason?.trim() &&
      (values.stage !== "Rejected" || rejectionRounds.includes(values.round))
    const invalidId = uniqueIds.find(id => !valid(individual[id] || {}))
    if (mode === "same" ? !valid({ stage, reason, round }) : invalidId != null) {
      setError(""); return
    }

    const companyId = Number(selectedCompanyId)
    if (!Number.isInteger(companyId) || companyId <= 0) {
      const message = "Please select a company before updating candidate stages."
      setError(message)
      await Swal.fire({ icon: "error", title: "Company Required", text: message, confirmButtonText: "OK" })
      return
    }

    requestLock.current = true
    setPending(true); setError(""); setOutcome(null)
    let processed = false
    try {
      const payload = mode === "same" ? {
        job_candidate_ids: uniqueIds, stage, reason: reason.trim(),
        ...(stage === "Rejected" ? { rejected_round: round } : {}),
      } : { updates: uniqueIds.map(id => ({
        job_candidate_id: id,
        stage: individual[id].stage,
        reason: individual[id].reason.trim(),
        ...(individual[id].stage === "Rejected" ? { rejected_round: individual[id].round } : {}),
      })) }
      const token = String(authToken || "").trim()
      const authorization = token
        ? (token.toLowerCase().startsWith("bearer ") ? token : `Bearer ${token}`)
        : undefined
      const requestConfig = {
        ...(authorization ? { headers: { Authorization: authorization } } : {}),
        params: { company_id: companyId },
      }
      const { data } = requestConfig
        ? await axios.post("/api/v1/bulk-change-stage/", payload, requestConfig)
        : await axios.post("/api/v1/bulk-change-stage/", payload)
      processed = true
      const records = Array.isArray(data?.results) ? data.results : []
      const successful = uniqueIds.filter(id => records.some(record =>
        applicationId(record.job_candidate_id) === id && record.status === "updated"))
      const failed = uniqueIds.filter(id => !successful.includes(id)).map(id => {
        const record = records.find(item => applicationId(item.job_candidate_id) === id)
        return { id, error: record?.error ? errorText(record.error) : "No confirmed update returned. Refresh before trying again." }
      })
      setOutcome({ updated: data.updated_count ?? successful.length, failedCount: data.failed_count ?? failed.length,
        failed: failed.map(item => ({ ...item, name: candidateName(item.id) })) })
      if (failed.some(item => !records.some(record => applicationId(record.job_candidate_id) === item.id && record.status === "failed"))) {
        setNeedsRefresh(true)
      }
      onProcessed(jobId, successful)
      let refreshFailed = false
      try { await onRefresh(jobId) } catch { refreshFailed = true }
      const updatedCount = Number(data.updated_count ?? successful.length)
      const failedCount = Number(data.failed_count ?? failed.length)
      const details = failed.map(item => `${candidateName(item.id)}: ${item.error}`).join("\n")
      onClose()
      const isFullSuccess = updatedCount > 0 && failedCount === 0
      const isPartialSuccess = updatedCount > 0 && failedCount > 0
      const resultMessage = isFullSuccess
        ? `Stage updated successfully for ${updatedCount} candidate(s).`
        : isPartialSuccess
          ? `${updatedCount} candidates updated and ${failedCount} failed.`
          : "No candidates were updated."
      await Swal.fire({
        icon: isFullSuccess ? "success" : isPartialSuccess ? "warning" : "error",
        title: isFullSuccess ? "Stages Updated" : isPartialSuccess ? "Partial Stage Update" : "Stage Update Failed",
        text: `${resultMessage}${details ? `\n\n${details}` : ""}${refreshFailed ? "\n\nUnable to refresh. Refresh the page before another submission." : ""}`,
        confirmButtonText: "OK",
      })
    } catch (failure) {
      let message
      if (!processed && failure.response?.status === 400) message = errorText(failure.response.data)
      else if (!processed && failure.response?.status === 403) message = "Access denied for this company. " + errorText(failure.response.data)
      else {
        setNeedsRefresh(true)
        message = processed ? "Batch processed, but refresh failed. Refresh before another submission." :
          "The result is uncertain. Some applications or notifications may already have been processed. Refresh and review before submitting again."
        try { await onRefresh(jobId) } catch { message += " Refresh failed. Reload the page before submitting again." }
      }
      setError(message)
      onClose()
      await Swal.fire({ icon: "error", title: "Unable to Change Stage", text: message, confirmButtonText: "OK" })
    } finally { requestLock.current = false; setPending(false) }
  }
  return <Modal isOpen={jobId != null} toggle={pending ? undefined : onClose} backdrop="static" centered scrollable size="lg" className="bulk-stage-dialog">
    <ModalHeader toggle={undefined} className="d-flex justify-content-between align-items-center">
      <span>Change Candidate Stage</span>
      <button
        type="button"
        className="btn btn-link p-0"
        aria-label="Close Change Candidate Stage"
        title="Close"
        onClick={onClose}
        disabled={pending}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          fontSize: 18,
          lineHeight: 1,
          textDecoration: "none",
        }}
      >
        <FaTimes aria-hidden="true" />
      </button>
    </ModalHeader>
    <form onSubmit={submit} noValidate>
      <ModalBody className="bulk-stage-body">
        <div className="bulk-stage-toolbar">
        {showSharedMode && <fieldset className="bulk-stage-modes" disabled={pending}>
          <legend className="visually-hidden">Stage update mode</legend>
          <label className="d-flex align-items-center gap-2"><input type="radio" name="bulk-stage-mode" value="same" checked={mode === "same"} onChange={() => setMode("same")} />Same stage and reason</label>
          <label className="d-flex align-items-center gap-2"><input type="radio" name="bulk-stage-mode" value="different" checked={mode === "different"} onChange={() => setMode("different")} />Different stage and reason</label>
        </fieldset>}
        <p className="bulk-stage-count">Selected candidates: <strong>{uniqueIds.length}</strong></p>
        </div>
        {mode === "same" && candidates.length > 0 && <div className="bulk-stage-candidates">
          {candidates.map(candidate => <div key={candidate.id} className="bulk-stage-candidate" style={{ overflowWrap: "anywhere" }}>
            <div className="fw-semibold bulk-stage-candidate-name">{candidate.name || "--"}</div>
            <div className="small"><span className="text-muted">Current Stage: </span>{candidate.stage || "--"}</div>
            <div className="small"><span className="text-muted">Current Reason: </span>{candidate.reason || "--"}</div>
          </div>)}
        </div>}
        {error && <div className="alert alert-danger" role="alert" style={{ overflowWrap: "anywhere" }}>{error}</div>}
        {outcome && <div aria-live="polite">
          <p className="fw-semibold">Updated: {outcome.updated} | Failed: {outcome.failedCount}</p>
          {outcome.failed.map(item => <div key={item.id} className="alert alert-warning" style={{ overflowWrap: "anywhere" }}>{item.name}: {item.error}</div>)}
        </div>}
        {mode === "same" ? <div className="bulk-stage-shared-fields">
        <div className="bulk-stage-field">
        <label htmlFor="bulk-candidate-stage" className="form-label">Stage</label>
        <select id="bulk-candidate-stage" {...fieldProps("bulk-candidate-stage", "stage", stage, "form-select")} value={stage} onChange={e => setStage(e.target.value)} required disabled={pending}>
          <option value="">Select stage</option>
          {stages.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
        </select>
        {fieldError("bulk-candidate-stage", "stage", stage)}
        </div>
        <div className="bulk-stage-field bulk-stage-reason">
        <label htmlFor="bulk-candidate-reason" className="form-label">Reason</label>
        <input type="text" id="bulk-candidate-reason" {...fieldProps("bulk-candidate-reason", "reason", reason, "form-control")} value={reason} onChange={e => setReason(e.target.value)} required disabled={pending} />
        {fieldError("bulk-candidate-reason", "reason", reason)}
        </div>
        {stage === "Rejected" && <div className="bulk-stage-field">
          <label htmlFor="bulk-rejected-round" className="form-label">Rejected Round</label>
          <select id="bulk-rejected-round" {...fieldProps("bulk-rejected-round", "round", round, "form-select")} value={round} onChange={e => setRound(e.target.value)} required disabled={pending}>
            <option value="">Select rejected round</option>
            {rejectionRounds.map(item => <option key={item}>{item}</option>)}
          </select>
          {fieldError("bulk-rejected-round", "round", round)}
        </div>}
        </div> : <div className="bulk-stage-table-scroll"><table className="bulk-stage-table">
          <thead><tr><th scope="col">Candidate</th><th scope="col">Current Stage</th><th scope="col">Current Reason</th><th scope="col">New Stage</th><th scope="col">New Reason</th></tr></thead>
          <tbody>{uniqueIds.map((id, index) => {
          const values = individual[id] || {}
          const current = candidates.find(candidate => applicationId(candidate.id) === id)
          return <tr key={id}>
            <th scope="row">
              <div className="bulk-stage-table-name"><span className="bulk-stage-row-number">{index + 1}</span><span>{candidateName(id)}</span></div>
            </th>
              <td className="bulk-stage-current-cell"><span className="bulk-stage-current-status">{current?.stage || "--"}</span></td>
              <td className="bulk-stage-current-cell"><div className="bulk-stage-previous-reason">{current?.reason || "--"}</div></td>
              <td>
                <label htmlFor={`bulk-stage-${id}`} className="visually-hidden">Stage</label>
                <select id={`bulk-stage-${id}`} {...fieldProps(`bulk-stage-${id}`, "stage", values.stage, "form-select")} disabled={pending} required value={values.stage || ""} onChange={event => updateIndividual(id, "stage", event.target.value)}>
                  <option value="">Select stage</option>
                  {stages.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
                </select>
                {fieldError(`bulk-stage-${id}`, "stage", values.stage)}
                {values.stage === "Rejected" && <div className="bulk-stage-round-field">
                  <label htmlFor={`bulk-round-${id}`} className="form-label">Rejected Round</label>
                  <select id={`bulk-round-${id}`} {...fieldProps(`bulk-round-${id}`, "round", values.round, "form-select")} disabled={pending} required value={values.round || ""} onChange={event => updateIndividual(id, "round", event.target.value)}>
                    <option value="">Select rejected round</option>
                    {rejectionRounds.map(item => <option key={item}>{item}</option>)}
                  </select>
                  {fieldError(`bulk-round-${id}`, "round", values.round)}
                </div>}
              </td>
              <td>
                <label htmlFor={`bulk-reason-${id}`} className="visually-hidden">Reason</label>
                <div className="bulk-stage-reason-editor">
                  <div className="bulk-stage-reason-measure" aria-hidden="true">{current?.reason || "--"}{"\n"}</div>
                  <div className="bulk-stage-reason-measure" aria-hidden="true">{values.reason || ""}{"\n"}</div>
                  <textarea id={`bulk-reason-${id}`} {...fieldProps(`bulk-reason-${id}`, "reason", values.reason, "form-control")} rows={3} disabled={pending} required value={values.reason || ""} onChange={event => updateIndividual(id, "reason", event.target.value)} />
                </div>
                {fieldError(`bulk-reason-${id}`, "reason", values.reason)}
              </td>
          </tr>
        })}</tbody></table></div>}
      </ModalBody>
      <ModalFooter className="justify-content-center">
        <Button type="button" color="secondary" disabled={pending} onClick={onClose}>Close</Button>
        {needsRefresh && <Button type="button" color="primary" disabled={pending} onClick={refresh}>Refresh Results</Button>}
        <Button type="submit" color="primary" disabled={pending || needsRefresh || !uniqueIds.length || uniqueIds.length > 100}>{pending ? "Processing..." : `Change stage (${uniqueIds.length})`}</Button>
      </ModalFooter>
    </form>
  </Modal>
}
